/**
 * BulkAttachPdfDialog — modal for the "Attach PDF" bulk action on the
 * Policies page. Uploads a single PDF and associates it with every
 * currently-selected policy in one call.
 *
 * This is an attachment-only flow — it never mutates policy content,
 * status, or version.
 */
import React, { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Paperclip, Upload, X, Loader2, FileType, ChevronDown, ChevronRight } from 'lucide-react';

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${t}` } };
};

const fmtSize = (n) =>
  n < 1024 ? `${n} B`
  : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB`
  : `${(n / 1024 / 1024).toFixed(1)} MB`;

const BulkAttachPdfDialog = ({ open, onOpenChange, selectedPolicies, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef(null);

  const count = selectedPolicies.length;

  const clear = () => { setFile(null); setBusy(false); setDragOver(false); setExpanded(false); };
  const close = () => { if (!busy) { onOpenChange(false); clear(); } };

  const takeFile = useCallback((f) => {
    if (!f) return;
    const okExt = /\.pdf$/i.test(f.name || '');
    const okMime = /^application\/(pdf|x-pdf)$/i.test(f.type || '');
    if (!okExt || !okMime) {
      toast.error('Only PDF files are accepted (.pdf, application/pdf).');
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error('PDF exceeds the 20 MB limit.');
      return;
    }
    setFile(f);
  }, []);

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    takeFile(e.dataTransfer?.files?.[0]);
  };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  const submit = async () => {
    if (!file || busy || count === 0) return;
    setBusy(true);
    try {
      // Upload once, receive a reusable attachment file record.
      const fd = new FormData();
      fd.append('file', file);
      const uploadResp = await axios.post(
        `/api/policy-attachments/upload`,
        fd,
        {
          ...authHeaders(),
          headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' },
        },
      );
      const fileId = uploadResp.data.id;
      // Link the same file to every selected policy in a single call.
      const linkResp = await axios.post(
        `/api/policies/attachments/bulk-attach`,
        { file_id: fileId, policy_ids: selectedPolicies.map((p) => p.id), source: 'bulk_attach' },
        authHeaders(),
      );
      const attached = linkResp.data?.attached_count || 0;
      const skipped = linkResp.data?.skipped || [];
      if (attached === 0) {
        toast.error('Could not attach to any policy. Check your permissions and try again.');
      } else if (skipped.length > 0) {
        toast.warning(
          `PDF attached to ${attached} of ${count} policies. ${skipped.length} skipped (no permission).`
        );
      } else {
        toast.success(`PDF attached to ${attached} polic${attached === 1 ? 'y' : 'ies'}.`);
      }
      onOpenChange(false);
      clear();
      if (onSuccess) onSuccess({ attached, skipped, fileId });
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Attachment failed. Please try again.';
      toast.error(String(detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="policy-bulk-attach-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="w-5 h-5 text-blue-600" /> Attach PDF to selected policies
          </DialogTitle>
          <DialogDescription>
            The same PDF will be attached to every selected policy. This is an
            attachment — policy content and versions are not modified.
          </DialogDescription>
        </DialogHeader>

        {/* Selected policies list (collapsible past 5 items) */}
        <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="policy-bulk-attach-selection">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="w-full flex items-center justify-between text-left"
            aria-expanded={expanded}
            data-testid="policy-bulk-attach-toggle-selection"
          >
            <span className="text-sm font-semibold text-slate-800">
              {count} polic{count === 1 ? 'y' : 'ies'} selected
            </span>
            <span className="flex items-center text-xs text-slate-500">
              {expanded ? (<>Hide list <ChevronDown className="w-3.5 h-3.5 ml-1" /></>) : (<>Show list <ChevronRight className="w-3.5 h-3.5 ml-1" /></>)}
            </span>
          </button>
          {expanded && (
            <ul className="mt-2 max-h-40 overflow-auto space-y-1 text-xs text-slate-700">
              {selectedPolicies.map((p) => (
                <li key={p.id} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                  <span className="truncate" title={p.name}>{p.name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* File picker / drop zone */}
        {!file ? (
          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            className={`mt-3 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${dragOver ? 'border-blue-400 bg-blue-50' : 'border-slate-300 bg-white'}`}
            data-testid="policy-bulk-attach-dropzone"
          >
            <Upload className="w-8 h-8 mx-auto text-slate-400" />
            <p className="mt-3 text-sm font-medium text-slate-700">Drag and drop your PDF here</p>
            <p className="text-xs text-slate-500">or</p>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={(e) => takeFile(e.target.files?.[0])}
              className="hidden"
              data-testid="policy-bulk-attach-input"
            />
            <Button
              type="button" variant="outline" size="sm"
              className="mt-2"
              onClick={() => inputRef.current?.click()}
              data-testid="policy-bulk-attach-browse-btn"
            >
              Browse files
            </Button>
            <p className="mt-3 text-[11px] text-slate-400">PDF only · up to 20 MB</p>
          </div>
        ) : (
          <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3" data-testid="policy-bulk-attach-selected-file">
            <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
              <FileType className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-800 truncate" title={file.name}>{file.name}</div>
              <div className="text-xs text-slate-500">{fmtSize(file.size)}</div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setFile(null)} disabled={busy} data-testid="policy-bulk-attach-remove-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}

        {file && (
          <p className="mt-3 text-xs text-slate-600" data-testid="policy-bulk-attach-confirm-copy">
            This PDF will be attached to all selected policies. Existing policy
            content and attachments will remain unchanged.
          </p>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={close} disabled={busy} data-testid="policy-bulk-attach-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={!file || busy || count === 0} data-testid="policy-bulk-attach-confirm-btn">
            {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Attaching…</>)
              : (<><Paperclip className="w-4 h-4 mr-2" /> Attach to {count} polic{count === 1 ? 'y' : 'ies'}</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkAttachPdfDialog;
